import { Flex, Group, Tabs, Title } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import { CampaignsTab } from "@/components/features/W9Requests/CampaignsTab";
import { OptoutsTab } from "@/components/features/W9Requests/OptoutsTab";
import { SendRequestsTab } from "@/components/features/W9Requests/SendRequestsTab";
import { SmsHistoryTab } from "@/components/features/W9Requests/SmsHistoryTab";
import { TrackerTab } from "@/components/features/W9Requests/TrackerTab";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { usePersistedTabs } from "@/hooks/usePersistedTabs";
import { useLeaveWhenInsideEntity } from "@/hooks/usePlatformView";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon, routeIconKeys } from "@/utils/getRouteIconComponent";

export const Route = createFileRoute("/_authenticated/service-providers/w9-requests/")({
  component: W9RequestsPage,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function W9RequestsPage() {
  useLeaveWhenInsideEntity();
  useBreadcrumb(
    [
      {
        title: "Service Providers",
        href: "/service-providers",
        icon: getRouteIcon(routeIconKeys.w9Requests, 16),
      },
      {
        title: "W-9 Requests",
        href: "/service-providers/w9-requests",
      },
    ],
    {},
  );

  const { activeTab, setActiveTab } = usePersistedTabs({ defaultTab: "send" });
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [sendCampaignId, setSendCampaignId] = useState<string | null>(null);

  const openCampaignInTracker = (id: string) => {
    setCampaignFilter(id);
    setActiveTab("tracker");
  };

  const addRequestsToCampaign = (id: string) => {
    setSendCampaignId(id);
    setActiveTab("send");
  };

  return (
    <Flex direction="column" px="var(--spacing-large)" py="var(--spacing-medium)">
      <Group justify="space-between" align="center">
        <Title order={3}>W-9 Requests</Title>
      </Group>

      <Tabs
        value={activeTab}
        onChange={(value) => {
          if (value === "send") setSendCampaignId(null);
          setActiveTab(value || "send");
        }}
      >
        <Tabs.List>
          <Tabs.Tab value="send">Send Requests</Tabs.Tab>
          <Tabs.Tab value="tracker">Tracker</Tabs.Tab>
          <Tabs.Tab value="campaigns">Campaigns</Tabs.Tab>
          <Tabs.Tab value="optouts">SMS Opt-outs</Tabs.Tab>
          <Tabs.Tab value="sms-history">SMS History</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="send">
          <SendRequestsTab key={sendCampaignId ?? "default"} initialCampaignId={sendCampaignId} />
        </Tabs.Panel>
        <Tabs.Panel value="tracker">
          <TrackerTab campaignFilter={campaignFilter} onCampaignFilterChange={setCampaignFilter} />
        </Tabs.Panel>
        <Tabs.Panel value="campaigns">
          <CampaignsTab
            onOpenCampaign={openCampaignInTracker}
            onAddRequests={addRequestsToCampaign}
          />
        </Tabs.Panel>
        <Tabs.Panel value="optouts">
          <OptoutsTab />
        </Tabs.Panel>
        <Tabs.Panel value="sms-history">
          <SmsHistoryTab isActive={activeTab === "sms-history"} />
        </Tabs.Panel>
      </Tabs>
    </Flex>
  );
}
